import React from 'react';
import { DownloadIcon, ExternalLinkIcon, RotateCcwIcon, PlusIcon, RefreshCwIcon, CheckCircle2Icon, AlertTriangleIcon } from 'lucide-react';
import type { DestinationId, DestinationRunStatus } from '../../lib/types';
import { DESTINATIONS } from '../../lib/destinations';
import { StatusBadge } from '../ui/StatusBadge';
import { cn, formatNumber } from '../../lib/utils';
interface Props {
  selected: DestinationId[];
  results: Record<DestinationId, DestinationRunStatus>;
  totalRecords: number;
  onStartAnother: () => void;
  onRetry: (id: DestinationId) => void;
}
export function ResultsView({
  selected,
  results,
  totalRecords,
  onStartAnother,
  onRetry
}: Props) {
  const failed = 14;
  const skipped = 8;
  const duplicates = 5;
  const created = totalRecords - failed - skipped;
  const anyFailed = selected.some(id => results[id] === 'failed');
  const allGood = selected.every(id => results[id] === 'completed');
  const stats = [{
    label: 'Records submitted',
    value: totalRecords
  }, {
    label: 'Teli AI queued',
    value: results.teli === 'failed' ? 0 : created
  }, {
    label: 'SendGrid enrolled',
    value: results.sendgrid === 'failed' ? 0 : created
  }, {
    label: 'Five9 uploaded',
    value: results.five9 === 'failed' ? 0 : created
  }, {
    label: 'Skipped',
    value: skipped
  }, {
    label: 'Failed',
    value: failed
  }, {
    label: 'Duplicates',
    value: duplicates
  }, {
    label: 'Duration',
    value: '2m 14s',
    raw: true
  }];
  return <div className="space-y-6" data-id="element-395" data-mp="1a4woqc4jtesy">
      {/* Headline */}
      <div className={cn('flex items-center gap-4 rounded-2xl border p-5', allGood ? 'border-emerald-200 bg-emerald-50' : 'border-amber-200 bg-amber-50')} data-id="element-396" data-mp="1a7iv48fpdo9l">
        <div className={cn('flex h-12 w-12 shrink-0 items-center justify-center rounded-full', allGood ? 'bg-emerald-100' : 'bg-amber-100')} data-id="element-397" data-mp="1p1xh8hukddsh">
          {allGood ? <CheckCircle2Icon className="h-6 w-6 text-emerald-600" data-id="element-398" data-mp="ug9ovivaey1f" /> : <AlertTriangleIcon className="h-6 w-6 text-amber-600" data-id="element-399" data-mp="194cxl00psntx" />}
        </div>
        <div data-id="element-400" data-mp="14cuua5b7yb5s">
          <h3 className="text-lg font-semibold text-gray-900" data-id="element-401" data-mp="14cd33n3eyn4q">
            {allGood ? 'Upload completed successfully' : 'Upload finished with some issues'}
          </h3>
          <p className="text-sm text-gray-600" data-id="element-402" data-mp="1d4ofw65luc28">
            {anyFailed ? 'Some destinations completed while others failed. Each result is shown independently below.' : 'All records were processed. Review per-destination results below.'}
          </p>
        </div>
      </div>

      {/* Per-destination results (independent) */}
      <div className="grid gap-4 lg:grid-cols-3" data-id="element-403" data-mp="3tw6mydtto6vv">
        {selected.map(id => {
        const def = DESTINATIONS.find(d => d.id === id)!;
        const st = results[id];
        const isFailed = st === 'failed';
        return <div key={id} className={cn('rounded-2xl border bg-white p-5 shadow-card', isFailed ? 'border-red-200' : 'border-gray-200')} data-id="element-404" data-mp="29b9mvuzjqoza">
              <div className="flex items-center justify-between" data-id="element-405" data-mp="1zkneakq8yyq3">
                <div className="flex items-center gap-3" data-id="element-406" data-mp="1lmgwee1p19c2">
                  <img src={def.icon} alt="" className="h-10 w-10 rounded-lg" data-id="element-407" data-mp="1lp336bgrfkt8" />
                  <p className="text-sm font-semibold text-gray-900" data-id="element-408" data-mp="1pm7lmdbknheh">
                    {def.name}
                  </p>
                </div>
                <StatusBadge status={st} size="sm" data-id="element-409" data-mp="1gn42xagbh40i" />
              </div>
              <div className="mt-4 space-y-1.5 text-sm" data-id="element-410" data-mp="1gpgqc34ug6ky">
                <Row label="Created" value={isFailed ? '0' : formatNumber(created)} data-id="element-411" data-mp="363vcqgse3hda" />
                <Row label="Skipped" value={isFailed ? '—' : formatNumber(skipped)} data-id="element-412" data-mp="3azaon36u7372" />
                <Row label="Failed" value={isFailed ? formatNumber(totalRecords) : formatNumber(failed)} danger={isFailed} data-id="element-413" data-mp="1602n94rmh7tm" />
              </div>
              {isFailed && <button type="button" onClick={() => onRetry(id)} className="mt-4 inline-flex w-full items-center justify-center gap-1.5 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs font-semibold text-red-700 transition-colors hover:bg-red-100" data-id="element-414" data-mp="s12x1sogokvc">
                  <RefreshCwIcon className="h-3.5 w-3.5" data-id="element-415" data-mp="eafvztv8aiw3" />
                  Retry {def.name} upload
                </button>}
            </div>;
      })}
      </div>

      {/* Overall stats */}
      <section className="rounded-2xl border border-gray-200 bg-white p-5 shadow-card" data-id="element-416" data-mp="4bqpex8s8kv5">
        <h3 className="mb-4 text-sm font-semibold text-gray-900" data-id="element-417" data-mp="2fxsjr2v7wwna">
          Upload report
        </h3>
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4" data-id="element-418" data-mp="2kvu6pyvrlg41">
          {stats.map(s => <div key={s.label} className="rounded-xl bg-gray-50 p-3" data-id="element-419" data-mp="2pwzx3q1ycrae">
              <p className="text-xl font-bold text-gray-900" data-id="element-420" data-mp="2tyd1rwtb6ofq">
                {s.raw ? s.value : formatNumber(Number(s.value))}
              </p>
              <p className="text-xs font-medium text-gray-500" data-id="element-421" data-mp="1x5q2bpar5cbz">{s.label}</p>
            </div>)}
        </div>
      </section>

      {/* Actions */}
      <div className="flex flex-wrap items-center justify-between gap-3" data-id="element-422" data-mp="267g2pkmm3llw">
        <div className="flex flex-wrap items-center gap-2" data-id="element-423" data-mp="2ayz9djb89lrc">
          <ActionBtn icon={DownloadIcon} label="Download full report" data-id="element-424" data-mp="2b1bxyfbhqudd" />
          <ActionBtn icon={DownloadIcon} label="Download failed records" data-id="element-425" data-mp="1egjp2hsq9cxk" />
          <ActionBtn icon={ExternalLinkIcon} label="View Salesforce leads" data-id="element-426" data-mp="1iiespsy4s2ei" />
          {anyFailed && <button type="button" onClick={() => selected.filter(id => results[id] === 'failed').forEach(onRetry)} className="inline-flex items-center gap-1.5 rounded-lg border border-amber-300 bg-amber-50 px-4 py-2.5 text-sm font-medium text-amber-700 transition-colors hover:bg-amber-100" data-id="element-427" data-mp="2lt29psbv9ayr">
              <RotateCcwIcon className="h-4 w-4" data-id="element-428" data-mp="22vrecitcv1ol" />
              Retry failed destinations
            </button>}
        </div>
        <button type="button" onClick={onStartAnother} className="inline-flex items-center gap-2 rounded-lg bg-salesforce-500 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-salesforce-600" data-id="element-429" data-mp="1xy7yxfkx8iv2">
          <PlusIcon className="h-4 w-4" data-id="element-430" data-mp="3n9i5tzpjxbhf" />
          Start another upload
        </button>
      </div>
    </div>;
}
function Row({
  label,
  value,
  danger
}: {
  label: string;
  value: string;
  danger?: boolean;
}) {
  return <div className="flex justify-between" data-id="element-431" data-mp="3qutoumsjwi1i">
      <span className="text-gray-500" data-id="element-432" data-mp="3vs3pkyle4g4n">{label}</span>
      <span className={cn('font-semibold', danger ? 'text-red-600' : 'text-gray-900')} data-id="element-433" data-mp="3vpqz1x1kgxfz">
        {value}
      </span>
    </div>;
}
function ActionBtn({
  icon: Icon,
  label
}: {
  icon: React.ElementType;
  label: string;
}) {
  return <button type="button" className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-4 py-2.5 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-50" data-id="element-434" data-mp="demcrpdt1p3b">
      <Icon className="h-4 w-4" data-id="element-435" data-mp="3nrh5ueo5d57" />
      {label}
    </button>;
}